import React, { useState, FC, Children, CSSProperties, useRef, useEffect } from 'react';
import SwiperType from 'swiper';
import { Navigation, Pagination, Autoplay, A11y } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import 'swiper/css/navigation'; // eslint-disable-line import/no-unresolved
import 'swiper/css/pagination'; // eslint-disable-line import/no-unresolved
import 'swiper/css/a11y'; // eslint-disable-line import/no-unresolved
import 'swiper/css/autoplay'; // eslint-disable-line import/no-unresolved
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import CondensedSliderNavigation from './condensed-banner-slider-navigation';
import SliderNavigation, { SliderNavigationProps } from './slider-navigation';

export type SliderProps = SliderNavigationProps & {
  allowArrowsOnTouchDevice?: boolean;
  className?: string;
  uniqueFFIDText?: string;
  containerClassName?: string;
  layoutRef?: React.RefObject<HTMLElement | null>;
  useLayoutRefMarginBox?: boolean;
  useViewportLayoutRef?: boolean;
  spaceBetween?: any;
  slidesPerView?: number | 'auto';
  dots?: boolean;
  fitToSlides?: boolean;
  slideWidth?: number;
  withThumbs?: boolean;
  slideWidthIsFlexible?: boolean;
  ariaLabelOfPreviousButton?: string;
  ariaLabelOfNextButton?: string;
  navPosition?: 'left' | 'right';
  gridNavigation?: boolean;
  blackBGForArrows?: boolean;
  cardArrows?: boolean;
  arrowsClassName?: string;
  slideDescription?: string;
} & SwiperProps;

/**
 * @param {object} props
 * @param {boolean} props.layoutRef An element used as a horizontal guide for the slider. The
 * slider will change its horizontal dimensions to those of the element, to use as area for
 * the track for the slides. Calculates slidesOffsetBefore on Swiper to align the slides back
 * to the content that the slider flows with. Does not calculate slidesOffsetAfter (just passes
 * in 0) because Swiper has an issue with how it calculates slide widths when both are provided.
 * (Currently we only use this to extend the slider's scrolling track area outside of its
 * container, so this is not an issue.) Overrides slidesOffsetBefore and slidesOffsetAfter.
 * @param {boolean} props.useLayoutRefMarginBox Whether to consider the computed margins
 * of the layoutRef when it's used, because the bounding rect only includes the content box.
 * @param {boolean} props.useViewportLayoutRef Whether to use the viewport as the layoutRef.
 * Overrides layoutRef.
 */
const Slider: FC<SliderProps> = ({
  className = '',
  containerClassName = '',
  slideWidth = 300,
  slidesPerView,
  fitToSlides = false,
  arrows = false,
  dots = false,
  spaceBetween = 20,
  children,
  onInit,
  slidesOffsetBefore,
  slidesOffsetAfter,
  allowTouchMove,
  prevArrowStyles = {},
  nextArrowStyles = {},
  slideWidthIsFlexible,
  ariaLabelOfPreviousButton,
  ariaLabelOfNextButton,
  allowArrowsOnTouchDevice = false,
  navPosition = 'right',
  gridNavigation = true,
  blackBGForArrows = false,
  uniqueFFIDText,
  layoutRef,
  useLayoutRefMarginBox = false,
  useViewportLayoutRef = false,
  cardArrows,
  arrowsClassName = '',
  slideDescription,
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  const [init, setInit] = useState(false);
  const [layoutMetrics, setLayoutMetrics] = useState({ swiperMarginLeft: 0, width: 0, before: 0 });
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const useLayoutRef = !!layoutRef || useViewportLayoutRef;

  // Keep the slider root as the alignment target, and extend only Swiper to layoutRef bleed.
  const updateLayoutMetrics: () => void = () => {
    window.requestAnimationFrame(() => {
      const layoutElement = layoutRef?.current;
      const sliderRoot = sliderContainerRef.current;

      if (!sliderRoot || !useLayoutRef) {
        setLayoutMetrics({ swiperMarginLeft: 0, width: 0, before: 0 });
        return;
      }

      const sliderRect = sliderRoot.getBoundingClientRect();
      let layoutRect = sliderRect;

      if (useViewportLayoutRef) {
        layoutRect = {
          left: 0,
          right: document.documentElement.clientWidth || window.innerWidth,
        } as DOMRect;
      } else if (layoutElement) {
        layoutRect = layoutElement.getBoundingClientRect();
      } // React mounting hasn't finished

      // These are the edges (x coordinates in the page) that the
      // Swiper scrolling area is expanded to.
      let bleedLeft = layoutRect.left;
      let bleedRight = layoutRect.right;

      if (layoutElement && useLayoutRefMarginBox) {
        // Expand to layoutRef margin box when requested.
        const layoutStyle = window.getComputedStyle(layoutElement);
        bleedLeft = layoutRect.left - Number.parseFloat(layoutStyle.marginLeft);
        bleedRight = layoutRect.right + Number.parseFloat(layoutStyle.marginRight);
      }

      setLayoutMetrics({
        swiperMarginLeft: bleedLeft - sliderRect.left,
        width: bleedRight - bleedLeft,
        before: sliderRect.left - bleedLeft,
      });
    });
  };

  useEffect(() => {
    // Have to handle layoutRef being unset.
    updateLayoutMetrics();

    if (!useLayoutRef) {
      return;
    }

    window.addEventListener('resize', updateLayoutMetrics);

    return () => {
      window.removeEventListener('resize', updateLayoutMetrics);
    };
  }, [layoutRef, useLayoutRefMarginBox, useViewportLayoutRef]);

  const handleInit = (swiper: SwiperType) => {
    setInit(true);
    onInit?.(swiper);
  };

  const navigationPrevRef = useRef<HTMLButtonElement>(null);
  const navigationNextRef = useRef<HTMLButtonElement>(null);

  // TODO: this is not used anywhere in the code base as of now... no usage of fitToSlides being truthy exists
  // so the sliderWidth is never calculated but made to be 100%.
  const validToFit: boolean = !!fitToSlides && !!slideWidth && !!slidesPerView && slidesPerView !== 'auto';
  let sliderWidth: CSSProperties['width'] = '100%';
  if (validToFit && slidesPerView && slidesPerView !== 'auto') {
    sliderWidth = `${spaceBetween * (slidesPerView - 1) + slideWidth * slidesPerView}px`;
  }

  const containerClassNames = useClassNames([
    containerClassName,
    'slider_container relative',
    !init ? 'invisible' : 'visible',
    {
      'slider__container--fit': validToFit,
    },
  ]);
  const slidesClassName = useClassNames(['slider', className]);

  const slideProps = {
    width: slideWidthIsFlexible ? 'fit-content' : `${slideWidth}px`,
  } as React.CSSProperties;

  const computedSwiperStyle = useLayoutRef
    ? {
        width: layoutMetrics.width || sliderWidth,
        marginLeft: layoutMetrics.width ? `${layoutMetrics.swiperMarginLeft}px` : undefined,
      }
    : { width: sliderWidth };

  const computedSlidesOffsetBefore = useLayoutRef ? layoutMetrics.before : slidesOffsetBefore;
  const computedSlidesOffsetAfter = useLayoutRef ? 0 : slidesOffsetAfter;

  const slides = Children.map(children, (child) => <SwiperSlide style={slideProps}>{child}</SwiperSlide>);

  return (
    <div ref={sliderContainerRef} className={containerClassNames}>
      <Swiper
        key={`${allowTouchMove} ${isTouchDevice}`}
        className={slidesClassName}
        modules={[Navigation, Pagination, Autoplay, A11y]}
        pagination={dots ? { clickable: true, bulletActiveClass: 'slider__bullet--active' } : false}
        slidesPerView={slidesPerView ?? 'auto'}
        spaceBetween={spaceBetween}
        style={computedSwiperStyle}
        slidesOffsetBefore={computedSlidesOffsetBefore ?? 0}
        slidesOffsetAfter={computedSlidesOffsetAfter ?? 0}
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        onInit={handleInit}
        allowTouchMove={allowTouchMove && isTouchDevice}
        data-ffid={`ctr-swiper${uniqueFFIDText ? '-' + uniqueFFIDText : ''}`}
        {...props}
      >
        {slides}
      </Swiper>
      {gridNavigation ? (
        <SliderNavigation
          cardArrows={cardArrows}
          arrows={allowArrowsOnTouchDevice ? arrows : arrows && !isTouchDevice}
          prevArrowStyles={prevArrowStyles}
          nextArrowStyles={nextArrowStyles}
          ariaLabelOfPreviousButton={ariaLabelOfPreviousButton}
          ariaLabelOfNextButton={ariaLabelOfNextButton}
          navigationPrevRef={navigationPrevRef}
          navigationNextRef={navigationNextRef}
          position={navPosition}
          uniqueFFIDText={uniqueFFIDText}
          arrowsClassName={arrowsClassName}
          slideDescription={slideDescription}
        />
      ) : (
        <CondensedSliderNavigation
          arrows={allowArrowsOnTouchDevice ? arrows : arrows && !isTouchDevice}
          navigationPrevRef={navigationPrevRef}
          navigationNextRef={navigationNextRef}
          blackBG={blackBGForArrows}
        />
      )}
    </div>
  );
};

export default Slider;
